import { ref, readonly } from 'vue';

export type ConsentCategory = 'analytics' | 'marketing';

export interface ConsentState {
    analytics: boolean;
    marketing: boolean;
}

const COOKIE_NAME = 'nv_consent';
const COOKIE_DAYS = 365;

const consentGiven = ref(false);
const consentState = ref<ConsentState>({ analytics: false, marketing: false });

function readCookie(): ConsentState | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
    if (!match) return null;
    try {
        const parsed = JSON.parse(decodeURIComponent(match[1]));
        if (typeof parsed.analytics === 'boolean' && typeof parsed.marketing === 'boolean') {
            return parsed;
        }
    } catch {
        // Invalid cookie, treat as no consent
    }
    return null;
}

function writeCookie(state: ConsentState): void {
    if (typeof document === 'undefined') return;
    const expires = new Date(Date.now() + COOKIE_DAYS * 864e5).toUTCString();
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(state))};expires=${expires};path=/;SameSite=Lax;Secure`;
}

function pushConsentUpdate(state: ConsentState): void {
    if (typeof window === 'undefined') return;
    if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
            analytics_storage: state.analytics ? 'granted' : 'denied',
            ad_storage: state.marketing ? 'granted' : 'denied',
            ad_user_data: state.marketing ? 'granted' : 'denied',
            ad_personalization: state.marketing ? 'granted' : 'denied',
        });
    }
}

function init(): void {
    const stored = readCookie();
    if (stored) {
        consentState.value = stored;
        consentGiven.value = true;
        pushConsentUpdate(stored);
    }
}

function acceptAll(): void {
    const state: ConsentState = { analytics: true, marketing: true };
    consentState.value = state;
    consentGiven.value = true;
    writeCookie(state);
    pushConsentUpdate(state);
}

function rejectAll(): void {
    const state: ConsentState = { analytics: false, marketing: false };
    consentState.value = state;
    consentGiven.value = true;
    writeCookie(state);
    pushConsentUpdate(state);
}

function savePreferences(state: ConsentState): void {
    consentState.value = state;
    consentGiven.value = true;
    writeCookie(state);
    pushConsentUpdate(state);
}

// Initialize on first import
init();

export function useCookieConsent() {
    return {
        consentGiven: readonly(consentGiven),
        consentState: readonly(consentState),
        acceptAll,
        rejectAll,
        savePreferences,
    };
}
